<script setup lang="ts">
/**
 * Approve a refund request — operator optionally adds notes for the
 * audit trail. Approving credits the investor's wallet immediately and
 * writes the ledger row (reference_type=order, reason=order_refund).
 *
 * Per the BE contract the wallet-method refund collapses pending →
 * settled in one transactional step (no separate "approved" pause).
 */
import { computed, ref, watch } from 'vue'
import Dialog from '@uiKit/elements/Dialog.vue'
import Box from '@uiKit/elements/Box.vue'
import Text from '@uiKit/elements/Text.vue'
import Button from '@uiKit/elements/Button.vue'
import Textarea from '@uiKit/elements/Textarea.vue'
import Message from '@uiKit/elements/Message.vue'
import FormField from '@uiKit/components/FormField.vue'
import { formatMoney } from '@shared/utils'

interface Props {
  visible: boolean
  amount: string
  currencyCode: string
  investorName: string
  loading?: boolean
  apiError?: string | null
}

const props = withDefaults(defineProps<Props>(), {
  loading: false,
  apiError: null,
})

const emit = defineEmits<{
  (e: 'update:visible', value: boolean): void
  (e: 'confirm', value: { notes: string }): void
}>()

const notes = ref('')

const hasEditedSinceApiError = ref(false)
watch(
  () => props.apiError,
  () => {
    hasEditedSinceApiError.value = false
  },
)
const visibleApiError = computed(() =>
  props.apiError && !hasEditedSinceApiError.value ? props.apiError : '',
)

watch(
  () => props.visible,
  (isVisible) => {
    if (!isVisible) {
      notes.value = ''
    }
  },
)

const amountLabel = computed(() => formatMoney(props.currencyCode, props.amount))

function handleConfirm() {
  emit('confirm', { notes: notes.value.trim() })
}

function handleInput(value: string) {
  notes.value = value
  hasEditedSinceApiError.value = true
}

function handleClose() {
  if (props.loading) return
  emit('update:visible', false)
}
</script>

<template>
  <Dialog
    :visible="visible"
    width="520px"
    max-height="80vh"
    :show-close-icon="!loading"
    :dismissable="!loading"
    @update:visible="(v) => !v && handleClose()"
  >
    <template #header>
      <Text type="page-title">Approve refund</Text>
    </template>

    <Box flex direction="col" gap="3">
      <Box class="confirm-summary" padding="3">
        <Text type="body-sm">
          Approve refund of
          <Text is="span" type="body-sm" weight="semibold">{{ amountLabel }}</Text>
          to
          <Text is="span" type="body-sm" weight="semibold">{{ investorName }}</Text>
          ? Funds will be credited to their wallet immediately.
        </Text>
      </Box>

      <FormField label="Notes (optional)">
        <Textarea
          :model-value="notes"
          :rows="3"
          placeholder="Any internal context for the audit trail (optional)."
          :disabled="loading"
          @update:model-value="handleInput"
        />
      </FormField>

      <Message
        v-if="visibleApiError"
        color="danger"
        variant="outlined"
        size="small"
        :message="visibleApiError"
      />
    </Box>

    <template #footer>
      <Box flex justify="end" gap="2">
        <Button
          label="Cancel"
          variant="text"
          color="secondary"
          :disabled="loading"
          @click="handleClose"
        />
        <Button label="Approve refund" color="primary" :loading="loading" @click="handleConfirm" />
      </Box>
    </template>
  </Dialog>
</template>

<style scoped>
.confirm-summary {
  background: var(--p-primary-50);
  border: 1px solid var(--p-primary-200, var(--p-primary-100));
  border-radius: var(--p-border-radius-md);
}
</style>
